import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export interface StatusHistoryEntry {
  id: string;
  status_id: string;
  status?: string;
  created_at: string;
  changed_by: string | null;
  status_config?: {
    id: string;
    name: string;
    position: number;
    label?: string;
  };
}

export interface Profile {
  id: string;
  full_name: string;
  phone: string | null;
  email: string | null;
}

export interface Order {
  id: string;
  release_code: string;
  customer_name: string;
  customer_contact: string | null;
  device: string;
  device_brand: string | null;
  device_model: string | null;
  issue: string | null;
  failure_description: string | null;
  imei_serial: string | null;
  accessories: string | null;
  password_pattern: string | null;
  status_id: string;
  status: string;
  priority: 'normal' | 'urgent';
  estimated_date: string | null;
  assigned_to: string | null;
  branch_id: string;
  created_at: string;
  updated_at: string;
  internal_notes?: string;
  profiles?: Profile;
  status_history?: StatusHistoryEntry[];
  status_config?: { name: string; label: string };
}

interface UseOrderQueryResult {
  order: Order | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useOrderQuery(orderId: string | null): UseOrderQueryResult {
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refetch = () => setRefreshTrigger(prev => prev + 1);

  useEffect(() => {
    if (!orderId) {
      setOrder(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchOrder() {
      setIsLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await supabase
          .from('orders')
          .select(`
            *,
            profiles(*),
            status_history(*, status_config(*))
          `)
          .eq('id', orderId)
          .single();

        if (fetchError) {
          if (!cancelled) {
            setError(fetchError.message);
            setOrder(null);
          }
          return;
        }

        if (!cancelled) {
          setOrder(data as Order);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to fetch order');
          setOrder(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchOrder();

    return () => {
      cancelled = true;
    };
  }, [orderId, refreshTrigger]);

  return { order, isLoading, error, refetch };
}